import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  ServiceAgreementStatusEnum,
  type GetV1ServiceAgreementsWithCustomerResponse,
} from '../../../api/generated/pm';
import { getV1ServiceAgreementsWithCustomerOptions } from '../../../api/generated/pm/@tanstack/react-query.gen';
import type { DivisionScopedListQueryParams } from '../../../hooks/queries/useDivisionRollup';

/**
 * Query hook for the Invites page (`/customers/invites`, ticket 03): Service
 * Agreements the Dealer's Customers have invited it to review, including
 * expired ones (PRD D3), plus the Dealer's Divisions' invites when `scope`
 * asks for them (D4). See `../../../hooks/useListState` /
 * `../../../hooks/queries/useDivisionRollup` for the shared page/limit/search
 * and Division-rollup state this is built on.
 */

/** Return type of {@link useInvitesList}, named here so consumers (tests,
 * the page) import it instead of computing `ReturnType<typeof ...>`. */
export type UseInvitesListResult = UseQueryResult<GetV1ServiceAgreementsWithCustomerResponse>;

export function useInvitesList({
  page,
  limit,
  search,
  scope,
  selectedDivisionIds,
  enabled,
}: DivisionScopedListQueryParams): UseInvitesListResult {
  return useQuery({
    ...getV1ServiceAgreementsWithCustomerOptions({
      query: {
        page,
        limit,
        'filter[serviceAgreementStatus]': { in: ServiceAgreementStatusEnum.INVITED },
        scope,
        ...(search && { 'filter[customer.name]': { startswith: search } }),
        ...(selectedDivisionIds.length && {
          'filter[providerBusinessPartnerId]': { in: selectedDivisionIds.join(',') },
        }),
      },
    }),
    enabled,
    placeholderData: (previous) => previous,
  });
}
